import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, ChevronLeft, Loader2, Shield } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const InsuranceReportPage = () => {
  const [form, setForm] = useState({
    claim_type: 'auto',
    jurisdiction: 'us_federal',
    policy_type: '',
    claim_summary: '',
    policy_limits: '',
    deductible: '',
    coverage_issues: [],
    denial_reasons: [],
    damages: {},
    include_bad_faith_analysis: true
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const generate = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        policy_limits: form.policy_limits ? parseFloat(form.policy_limits) : null,
        deductible: form.deductible ? parseFloat(form.deductible) : null
      };
      const res = await axios.post(`${API}/practice/insurance-report`, payload);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="insurance-report-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Insurance Analysis</h1>
              <p className="text-muted-foreground">Coverage analysis and bad faith evaluation</p>
            </div>
          </div>
        </div>
        
        {!result ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle>Claim Information</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Claim Type</Label>
                      <Select value={form.claim_type} onValueChange={v => setForm(f => ({...f, claim_type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto</SelectItem>
                          <SelectItem value="property">Property</SelectItem>
                          <SelectItem value="liability">Liability</SelectItem>
                          <SelectItem value="health">Health</SelectItem>
                          <SelectItem value="life">Life</SelectItem>
                          <SelectItem value="disability">Disability</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Policy Type</Label>
                      <Input placeholder="e.g., Homeowners, Commercial Auto..." value={form.policy_type} onChange={e => setForm(f => ({...f, policy_type: e.target.value}))} />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Policy Limits ($)</Label>
                      <Input type="number" placeholder="100000" value={form.policy_limits} onChange={e => setForm(f => ({...f, policy_limits: e.target.value}))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Deductible ($)</Label>
                      <Input type="number" placeholder="1000" value={form.deductible} onChange={e => setForm(f => ({...f, deductible: e.target.value}))} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Claim Summary</Label>
                    <Textarea placeholder="Describe the claim, incident, and current status..." value={form.claim_summary} onChange={e => setForm(f => ({...f, claim_summary: e.target.value}))} rows={4} />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className="bg-blue-600 text-white border-0">
              <CardContent className="p-6">
                <Button className="w-full h-14 text-lg gap-2 bg-white text-blue-700 hover:bg-white/90" onClick={generate} disabled={loading || !form.claim_summary}>
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Analyzing...</> : <><Shield className="h-5 w-5" /> Generate Analysis</>}
                </Button>
                <label className="flex items-center gap-2 mt-4 cursor-pointer">
                  <input type="checkbox" checked={form.include_bad_faith_analysis} onChange={e => setForm(f => ({...f, include_bad_faith_analysis: e.target.checked}))} className="rounded" />
                  <span className="text-sm text-white/90">Include bad faith analysis</span>
                </label>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <Card className="bg-blue-600 text-white border-0">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold">Insurance Claim Analysis</h2>
                <p className="text-white/80">{result.claim_type} • {result.policy_type}</p>
                <Button variant="outline" onClick={() => setResult(null)} className="mt-4 text-white border-white/20">New Analysis</Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader><CardTitle>Coverage & Strategy Analysis</CardTitle></CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">{result.analysis}</div>
              </CardContent>
            </Card>
            
            {result.bad_faith_indicators && (
              <Card>
                <CardHeader><CardTitle>Bad Faith Indicators Checklist</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {result.bad_faith_indicators.map((indicator, i) => (
                      <li key={i} className="text-sm flex gap-2"><AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />{indicator}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};

// Case Summary Page

export default InsuranceReportPage;
